import { useQuery } from "@apollo/client/react";
import Link from "next/link";
import { useMemo } from "react";
import { CiCircleCheck } from "react-icons/ci";
import { LuExternalLink } from "react-icons/lu";

import { PollWeightType } from "@/backend/focus";
import { MaybeUserLabel } from "@/components/shared/maybe-user-label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AccountExtendedByPublicKeyDocument } from "@/graphql/codegen/graphql";
import type { Dictionary } from "@/utils/helpers";
import type { Maybe, PartialAccount } from "@/utils/types";

export type PollResult = {
  value: Maybe<string>;
  transactor: Maybe<PartialAccount>;
};

type PostPollResultsProps = {
  results: PollResult[];
  options: string[];
  pollType: PollWeightType;
  availableToVote: boolean;
  PollWeightTokenPublicKey: string;
  currentUserVote: PollResult | null;
};

const getValueBasedOnPollType = (
  pollType: PollWeightType,
  transactor: Maybe<PartialAccount>,
) => {
  if (!transactor) return 0;

  if (pollType === PollWeightType.desoBalance) {
    const balance = Number(transactor.desoBalance?.balanceNanos ?? 0);
    if (Number.isNaN(balance)) return 0;
    return balance;
  }
  if (pollType === PollWeightType.desoTokenBalance) {
    const tokenBalance = Number(
      transactor?.tokenBalances?.nodes?.[0]?.balanceNanos ?? 0,
    );
    if (Number.isNaN(tokenBalance)) return 0;
    return tokenBalance;
  }
  return 1;
};

export const PostPollResults = ({
  results,
  options,
  PollWeightTokenPublicKey,
  availableToVote,
  currentUserVote,
  pollType,
}: PostPollResultsProps) => {
  const resultStars = useMemo(() => {
    const voteCountByOption: Record<string, number> = {};
    results.forEach((result) => {
      if (result.value) {
        voteCountByOption[result.value] =
          (voteCountByOption[result.value] || 0) + 1;
      }
    });

    const total = results.reduce((totalSum: number, curr: PollResult) => {
      return totalSum + getValueBasedOnPollType(pollType, curr.transactor);
    }, 0);

    const calculateTransactorsPercentage = (
      questionResults: PollResult[],
      pollType: PollWeightType,
      total: number,
    ) => {
      const sumPercentage = questionResults.reduce((sum, result) => {
        return sum + getValueBasedOnPollType(pollType, result.transactor);
      }, 0);

      return total > 0 ? (sumPercentage / total) * 100 : 0;
    };

    return options.reduce((acc: Dictionary<number>, question: string) => {
      const questionResults = results.filter((x) => x.value === question);

      // If no votes or total is 0, use vote count percentage as fallback
      if (
        total === 0 &&
        Object.values(voteCountByOption).some((count) => count > 0)
      ) {
        const totalVotes = Object.values(voteCountByOption).reduce(
          (sum, count) => sum + count,
          0,
        );
        acc[question] =
          totalVotes > 0
            ? ((voteCountByOption[question] || 0) / totalVotes) * 100
            : 0;
      } else {
        acc[question] = calculateTransactorsPercentage(
          questionResults,
          pollType,
          total,
        );
      }

      return acc;
    }, {});
  }, [options, pollType, results]);

  return (
    <div className="flex flex-col gap-4">
      {!availableToVote && PollWeightTokenPublicKey && (
        <BuyOpenFundToken {...{ PollWeightTokenPublicKey }} />
      )}

      {Object.entries(resultStars).map(([option, count], index) => (
        <div
          key={`${option}-${index}`}
          // @ts-expect-error note this is custom css variable
          style={{ "--width": `${count}%` }}
          className="
            poll relative flex w-full flex-row justify-between overflow-hidden
            rounded-full border border-border pr-0 pl-2
            before:absolute before:top-0 before:left-0 before:flex before:h-full
            before:bg-card
            md:flex-row md:items-center
          "
        >
          <div className="relative flex items-center gap-3 pl-2">
            <p
              className="
                flex items-center gap-2 text-sm font-semibold
                text-muted-foreground
              "
            >
              {option}
              {currentUserVote?.value === option && (
                <CiCircleCheck className="text-xl text-emerald-400" />
              )}
            </p>
          </div>
          <div className="text-xs">
            <Button
              variant="ghost"
              className="
                relative z-10 flex items-center gap-2 text-xs
                text-muted-foreground
              "
            >
              <span className="inline-block font-mono">
                {Math.round(count)}%
              </span>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const BuyOpenFundToken = ({
  PollWeightTokenPublicKey,
}: {
  PollWeightTokenPublicKey: string;
}) => {
  const { data } = useQuery(AccountExtendedByPublicKeyDocument, {
    variables: {
      publicKey: PollWeightTokenPublicKey,
    },
  });

  const username = data?.accountByPublicKey?.username;

  return (
    <div
      className="
        relative rounded-2xl border border-border bg-card p-4 text-center
        text-sm
      "
    >
      <div className="mb-4 flex flex-col items-center gap-2">
        <div>
          To participate in this poll, you must hold the following token:
        </div>

        <Badge variant="outline" className="mt-2 pl-0.5">
          <MaybeUserLabel
            usernamePrefix="@"
            imageSize={22}
            clickable={false}
            value={PollWeightTokenPublicKey}
            className="flex items-center justify-center font-medium"
          />
        </Badge>
      </div>
      {username && (
        <Link
          target="_blank"
          href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${username}`}
          className="m-auto flex justify-center text-center"
        >
          <Button
            variant="outline"
            size="sm"
            className="flex items-center justify-center gap-2"
          >
            Buy on Openfund
            <LuExternalLink className="text-muted" />
          </Button>
        </Link>
      )}
    </div>
  );
};
